/**
 * 智能机器人代理 (BotAgent)
 * 支持字牌算牌、防守、拆牌保胡与特色方言趣味骚话
 */
import { Card, Player, BotDifficulty, AvailableActions, ActionType, ChiOption } from './types';
import { Rules } from './Rules';

export class BotAgent {
  private player: Player;
  private difficulty: BotDifficulty;
  private allDiscardedCards: Card[] = [];

  constructor(player: Player, difficulty: BotDifficulty = BotDifficulty.MEDIUM) {
    this.player = player;
    this.difficulty = difficulty;
  }

  public setDifficulty(difficulty: BotDifficulty): void {
    this.difficulty = difficulty;
  }

  public updateDiscards(discards: Card[]): void {
    this.allDiscardedCards = discards;
  }

  /**
   * 机器人对判定区卡牌进行操作决策
   */
  public async decideAction(
    targetCard: Card,
    actions: AvailableActions
  ): Promise<{ action: ActionType; chiOption?: ChiOption }> {
    await this.delay(this.getThinkingDelay());

    // 1. 如果能胡牌，必须立刻胡牌！(绝对优先级)
    if (actions.canHu) {
      return { action: ActionType.HU };
    }

    // 2. 如果触发强制性 提/跑/扫，自动执行
    if (actions.canTi) return { action: ActionType.TI };
    if (actions.canPao) return { action: ActionType.PAO };
    if (actions.canSao) return { action: ActionType.SAO };

    // 3. 碰牌决策
    if (actions.canPeng) {
      if (this.shouldPeng(targetCard)) {
        return { action: ActionType.PENG };
      }
    }

    // 4. 吃牌决策
    if (actions.canChi && actions.chiOptions.length > 0) {
      const chosenChi = this.chooseBestChiOption(actions.chiOptions);
      if (chosenChi) {
        return { action: ActionType.CHI, chiOption: chosenChi };
      }
    }

    // 5. 放弃 (过)
    return { action: ActionType.PASS };
  }

  /**
   * 评估是否应该碰牌
   */
  private shouldPeng(targetCard: Card): boolean {
    if (this.difficulty === BotDifficulty.EASY) {
      return Math.random() < 0.7;
    }

    // 中等/高级难度：
    // 大字碰有3胡，红牌更有利，优先碰
    if (targetCard.isBig || targetCard.isRed) return true;

    return true;
  }

  /**
   * 从多个吃牌方案中选取最优解
   */
  private chooseBestChiOption(options: ChiOption[]): ChiOption | null {
    if (options.length === 0) return null;

    if (this.difficulty === BotDifficulty.EASY) {
      return options[Math.floor(Math.random() * options.length)];
    }

    // 优先选择有胡息的吃牌 (壹贰叁 / 贰柒拾 = 6胡/3胡)
    const huxiOptions = options.filter(o => o.huxi > 0);
    if (huxiOptions.length > 0) {
      huxiOptions.sort((a, b) => b.huxi - a.huxi);
      return huxiOptions[0];
    }

    // 其次选择普通顺子
    return options[0];
  }

  /**
   * 智能选择手牌打出一张 (出牌决策)
   */
  public async decideDiscardCard(): Promise<Card | null> {
    if (this.player.hand.length === 0) return null;

    await this.delay(this.getThinkingDelay());

    if (this.difficulty === BotDifficulty.EASY) {
      return this.player.hand[Math.floor(Math.random() * this.player.hand.length)];
    }

    // 智能选牌算法：
    // 1. 统计手牌中每张牌的数量
    const counts = new Map<string, number>();
    this.player.hand.forEach(c => {
      const key = `${c.type}_${c.value}`;
      counts.set(key, (counts.get(key) || 0) + 1);
    });

    // 2. 严禁拆暗坎 (数量为3的牌绝不出)
    const candidateCards = this.player.hand.filter(c => {
      const key = `${c.type}_${c.value}`;
      return (counts.get(key) || 0) < 3;
    });

    if (candidateCards.length === 0) {
      return this.player.hand[0];
    }

    // 3. 保护好对子和 1-2-3 / 2-7-10 核心牌
    const safeCandidates = candidateCards.filter(c => {
      const count = counts.get(`${c.type}_${c.value}`) || 0;
      if (count >= 2) return false;
      if ([1, 2, 3, 7, 10].includes(c.value)) return false;
      return true;
    });

    if (safeCandidates.length > 0) {
      // 优先打场上已经出过的孤张 (熟张更安全)
      const matureCard = safeCandidates.find(c =>
        this.allDiscardedCards.some(dc => Rules.isSameCard(dc, c))
      );
      if (matureCard) return matureCard;

      return safeCandidates[0];
    }

    // 4. 次选：单张无搭散牌
    return candidateCards[0];
  }

  /**
   * 获取思考模拟延迟
   */
  private getThinkingDelay(): number {
    if (this.difficulty === BotDifficulty.EASY) return 600 + Math.random() * 500;
    if (this.difficulty === BotDifficulty.MEDIUM) return 800 + Math.random() * 600;
    return 1000 + Math.random() * 800;
  }

  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * 方言台词与骚话
   */
  public getBanter(action: 'win' | 'ti' | 'pao' | 'peng' | 'chi' | 'disappointed'): string {
    const banters: Record<string, string[]> = {
      win: [
        '承让承让，今天手风顺得很！🀄',
        '自摸一色红，各位掏荷包咯~💰',
        '这把牌叠得板板正正，胡得舒坦！🎉',
        '莫急莫急，打牌就是有输有赢嘛~😄'
      ],
      ti: [
        '起手就提，天命在我！🔥',
        '莫慌莫慌，看我来个开门红！🌀'
      ],
      pao: [
        '哈哈，这一张正合我意，跑一个！⚡',
        '这叫踏破铁鞋无觅处，来得正好！'
      ],
      peng: [
        '见红就碰，八方来财！✋',
        '碰一下，活络活络手气~'
      ],
      chi: [
        '这碗饭我吃下了，多谢下家赏饭！🍚',
        '搭子齐活，离胡牌不远咯~'
      ],
      disappointed: [
        '哎呀，差一张就胡了，居然被截胡！😅',
        '下把一定要把场子找回来！'
      ]
    };

    const list = banters[action] || banters.win;
    return list[Math.floor(Math.random() * list.length)];
  }
}
